import { uploadFile, type ApiClient, type Query, type UploadedFile } from '@saas/ui';
import {
  ATTACHMENT_FILE_SCOPE,
  type AddMembersInput,
  type ConversationDetail,
  type ConversationMemberSummary,
  type CreateChannelInput,
  type MessageDetail,
  type MyConversationsSummary,
  type MyInboxItem,
  type PostMessageInput,
  type RecordConversationItem,
  type RecordListQuery,
  type SharedItemsResult,
  type SubjectConversationView,
  type SystemPostSetting,
  type SystemPostSettingInput,
  type UpdateConversationInput,
} from '../api/conversation.dto.js';

/**
 * conversation の HTTP 契約（ブラウザ側）。型は api/ の DTO を使う（Date は JSON では ISO 文字列で届く）。
 * クエリキーはここで一元管理する（シェルが「自分宛て」の件数を出すときも同じキー）。
 */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export type RecordQuery = Partial<Omit<RecordListQuery, 'limit'>> & { limit?: number };

export const conversationKeys = {
  all: () => ['conversation'] as const,
  me: () => ['conversation', 'me'] as const,
  /** 「自分宛て」の一覧（未読の DM と自分宛てメンション・§12.10.3） */
  inbox: (limit: number) => ['conversation', 'me', 'inbox', limit] as const,
  /** システム投稿の種類ごとの ON / OFF（管理・§12.5-3） */
  systemPosts: () => ['conversation', 'admin', 'system-posts'] as const,
  records: (q: RecordQuery) => ['conversation', 'records', q] as const,
  subject: (subjectType: string, subjectId: string) => ['conversation', 'subject', subjectType, subjectId] as const,
  detail: (id: string) => ['conversation', 'detail', id] as const,
  messages: (id: string) => ['conversation', 'messages', id] as const,
  message: (messageId: string) => ['conversation', 'message', messageId] as const,
  replies: (rootId: string) => ['conversation', 'replies', rootId] as const,
  members: (id: string) => ['conversation', 'members', id] as const,
  /** 添付の署名付き URL（期限つき。画像のサムネイルで使い回す） */
  fileUrl: (fileId: string) => ['conversation', 'file-url', fileId] as const,
  sharedItems: (id: string) => ['conversation', 'shared-items', id] as const,
};

export const conversationApi = {
  me: (api: ApiClient) => api.get<MyConversationsSummary>('/conversation/me'),
  /** 「自分宛て」（未読の DM と自分宛てメンション。新しい順） */
  inbox: (api: ApiClient, limit: number) => api.get<MyInboxItem[]>('/conversation/me/inbox', { limit } as Query),
  /** 管理: システム投稿の種類ごとの ON / OFF（conversation.channel.manage） */
  systemPosts: (api: ApiClient) => api.get<SystemPostSetting[]>('/conversation/admin/system-posts'),
  setSystemPost: (api: ApiClient, body: SystemPostSettingInput) => api.patch<SystemPostSetting[]>('/conversation/admin/system-posts', body),
  records: (api: ApiClient, q: RecordQuery & { cursor?: string }) => api.get<Page<RecordConversationItem>>('/conversation/records', q as Query),
  subject: (api: ApiClient, subjectType: string, subjectId: string) => api.get<SubjectConversationView>(`/conversation/subject/${subjectType}/${subjectId}`),
  start: (api: ApiClient, subjectType: string, subjectId: string, title: string) => api.post<ConversationDetail>(`/conversation/subject/${subjectType}/${subjectId}/start`, { title }),
  createChannel: (api: ApiClient, body: CreateChannelInput) => api.post<ConversationDetail>('/conversation/channels', body),
  detail: (api: ApiClient, id: string) => api.get<ConversationDetail>(`/conversation/${id}`),
  update: (api: ApiClient, id: string, body: UpdateConversationInput) => api.patch<ConversationDetail>(`/conversation/${id}`, body),
  archive: (api: ApiClient, id: string) => api.post<ConversationDetail>(`/conversation/${id}/archive`),
  markRead: (api: ApiClient, id: string, lastReadMessageId?: string | null) => api.post<{ unread: number }>(`/conversation/${id}/read`, { lastReadMessageId: lastReadMessageId ?? null }),
  members: (api: ApiClient, id: string) => api.get<ConversationMemberSummary[]>(`/conversation/${id}/members`),
  addMembers: (api: ApiClient, id: string, body: AddMembersInput) => api.post<ConversationMemberSummary[]>(`/conversation/${id}/members`, body),
  leave: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/conversation/${id}/members/me`),
  setNotifyLevel: (api: ApiClient, id: string, notifyLevel: 'all' | 'mentions' | 'none') => api.patch<{ ok: true }>(`/conversation/${id}/members/me`, { notifyLevel }),
  messages: (api: ApiClient, id: string, q: { limit?: number; before?: string }) => api.get<Page<MessageDetail>>(`/conversation/${id}/messages`, q as Query),
  sharedItems: (api: ApiClient, id: string) => api.get<SharedItemsResult>(`/conversation/${id}/shared-items`),
  post: (api: ApiClient, id: string, body: PostMessageInput) => api.post<MessageDetail>(`/conversation/${id}/messages`, body),
  /** 発言 1 件（深いリンクのルート投稿を取る。読めない会話の発言は 404） */
  message: (api: ApiClient, messageId: string) => api.get<MessageDetail>(`/conversation/messages/${messageId}`),
  replies: (api: ApiClient, rootId: string) => api.get<MessageDetail[]>(`/conversation/messages/${rootId}/replies`),
  edit: (api: ApiClient, messageId: string, body: string) => api.patch<MessageDetail>(`/conversation/messages/${messageId}`, { body }),
  remove: (api: ApiClient, messageId: string) => api.del<{ ok: true }>(`/conversation/messages/${messageId}`),
  react: (api: ApiClient, messageId: string, emoji: string) => api.post<MessageDetail>(`/conversation/messages/${messageId}/reactions`, { emoji }),
  markThreadRead: (api: ApiClient, rootId: string) => api.post<{ ok: true }>(`/conversation/messages/${rootId}/read`),
  fileUrl: (api: ApiClient, fileId: string) => api.get<{ url: string }>(`/files/${fileId}/url`, { ttl: 900 }),
};

/** 添付: core の FileService（署名付き PUT）に scope = conversation / 会話 ID で上げる。投稿時に fileId を渡す */
export function uploadAttachment(api: ApiClient, conversationId: string, file: File): Promise<UploadedFile> {
  return uploadFile(api, file, { type: ATTACHMENT_FILE_SCOPE, id: conversationId });
}

/** 返信のクイックリアクション（段 3 は固定。絵文字ピッカーは段 4 の chat） */
export const QUICK_REACTIONS = ['👍', '✅', '🙏', '👀', '🎉'] as const;
